import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  File01Icon,
  Building01Icon,
  BankIcon,
  CheckmarkCircle02Icon,
  WalletIcon,
  AlertCircleIcon,
  Copy01Icon,
  ArrowDown01Icon,
} from '@hugeicons/core-free-icons';
import { Button } from '@components/Button/Button';
import { SaaeLogo } from '@components/SaaeLogo';
import { SaudiRiyalSymbol } from '@components/SaudiRiyalSymbol';
import { getDealClientView } from '@services/api';
import { queryKeys } from '../../queryKeys';
import { useToast, InlineSuccessCallout, InlineToastCallout } from '@contexts/ToastContext';
import { PayerRefundFlowModal } from '@components/payer/PayerRefundFlowModal';
import { PayerDealViewSkeleton } from '@components/skeletons/PayerDealViewSkeleton';
import { releaseEscrow } from '@services/api';
import { toEnglishDigits } from '@utils/englishDigits';
import { gregorianLocaleForLanguage } from '@utils/backofficeGregorian';

function formatSar(amount: number): string {
  // Always use Latin digits, even in Arabic UI.
  return new Intl.NumberFormat('en-SA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(isoDate: string, locale?: string): string {
  try {
    const d = new Date(isoDate + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return isoDate;
    const parts = new Intl.DateTimeFormat(gregorianLocaleForLanguage(String(locale ?? 'en')), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      calendar: 'gregory',
    }).formatToParts(d);
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    return toEnglishDigits([day, month, year].filter(Boolean).join(' '));
  } catch {
    return isoDate;
  }
}

/** Format API datetime (ISO) for payer confirmation line — calendar date in user locale. */
function formatConfirmedAt(isoDatetime: string, locale: 'ar' | 'en'): string {
  try {
    const d = new Date(isoDatetime);
    if (Number.isNaN(d.getTime())) return isoDatetime;
    const parts = new Intl.DateTimeFormat(gregorianLocaleForLanguage(locale === 'ar' ? 'ar' : 'en'), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      calendar: 'gregory',
    }).formatToParts(d);
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    return toEnglishDigits([day, month, year].filter(Boolean).join(' '));
  } catch {
    return isoDatetime;
  }
}

export interface PayerDealViewProps {
  dealId: string;
  token: string;
}

export function PayerDealView({ dealId, token }: PayerDealViewProps) {
  const { t, i18n } = useTranslation();
  const resolvedLng = ((i18n.resolvedLanguage || i18n.language || 'ar').split('-')[0] || 'ar') as 'ar' | 'en';
  const payerContentDir = resolvedLng === 'ar' ? 'rtl' : 'ltr';
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [copyDone, setCopyDone] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  const {
    data = null,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.payerDeal(dealId),
    queryFn: () => getDealClientView(dealId, token).then((r) => r.data),
    enabled: !!dealId,
  });

  const error = queryError
    ? ((queryError as { message?: string })?.message ?? t('common.error', { defaultValue: 'حدث خطأ.' }))
    : null;

  const handleConfirmPropertyTransfer = useCallback(async () => {
    setReleaseLoading(true);
    try {
      await releaseEscrow(dealId);
      addToast({
        title: t('payer.releaseSuccess', { defaultValue: 'Transaction completed successfully.' }),
        variant: 'success',
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.payerDeal(dealId) });
    } catch (e) {
      addToast({
        title: (e as { message?: string })?.message ?? t('common.error', { defaultValue: 'Something went wrong' }),
        variant: 'error',
      });
    } finally {
      setReleaseLoading(false);
    }
  }, [addToast, dealId, queryClient, t]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- intentional: manual memoization for clipboard handler
  const handleCopyIban = useCallback(() => {
    if (!data?.virtualIban) return;
    navigator.clipboard.writeText(data.virtualIban).then(
      () => {
        setCopyDone(true);
        addToast({
          title: t('payer.ibanCopied', { defaultValue: 'IBAN copied' }),
          description: data.virtualIban,
          variant: 'success',
        });
        setTimeout(() => setCopyDone(false), 2000);
      },
      () => {
        addToast({ title: t('payer.copyFailed', { defaultValue: 'Copy failed' }), variant: 'error' });
      },
    );
  }, [data?.virtualIban, addToast, t]);

  if (loading) {
    return (
      <div className="w-full max-w-2xl py-2" aria-busy="true" aria-live="polite">
        <PayerDealViewSkeleton />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full max-w-2xl flex flex-col items-center gap-4 py-12">
        <p className="font-sans text-surface-error-primary" role="alert">
          {error ?? t('payer.dealNotFound', { defaultValue: 'Deal information could not be found.' })}
        </p>
      </div>
    );
  }

  const isPaid = data.commStatus === 'Paid';
  const refundAlreadyRequested = Boolean(data.refundRequested);
  const isRefunded = Boolean(data.refunded);
  const transferConfirmedAt = data.propertyTransferConfirmedAt ?? null;
  const isTransferConfirmed = Boolean(transferConfirmedAt);

  return (
    <div className="w-full max-w-2xl flex flex-col items-center gap-6">
      <div className="flex flex-col items-center mb-2">
        <SaaeLogo size="large" mode="light" className="!h-[39px] w-auto" />
      </div>

      {/* Transaction Details */}
      <section
        className="w-full rounded-card border border-surface-border bg-surface-cards p-6 sm:p-8 flex flex-col gap-4"
        aria-labelledby="payer-transaction-heading"
      >
        <h2 id="payer-transaction-heading" className="font-sans text-lg font-semibold text-primary flex items-center gap-2 m-0">
          <HugeiconsIcon icon={File01Icon} size={22} className="text-primary" />
          {t('payer.transactionDetails', { defaultValue: 'Transaction Details' })}
        </h2>
        <dl className="grid gap-2 font-sans text-sm m-0">
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.referenceNumber', { defaultValue: 'Reference Number' })}</dt>
            <dd className="font-medium text-primary" dir="ltr">{data.referenceNumber}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.creationDate', { defaultValue: 'Creation Date' })}</dt>
            <dd className="font-medium text-primary">{formatDate(data.creationDate, i18n.language)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.leadBroker', { defaultValue: 'Lead Broker' })}</dt>
            <dd className="font-medium text-primary">{data.leadBrokerName}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.brokerMobile', { defaultValue: 'Broker Mobile' })}</dt>
            <dd className="font-medium text-primary" dir="ltr">{data.leadBrokerMobile}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.participatingBrokers', { defaultValue: 'Participating Brokers' })}</dt>
            <dd className="font-medium text-primary">{data.participatingBrokers}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.netCommission', { defaultValue: 'Net Commission' })}</dt>
            <dd className="font-medium text-primary tabular-nums">
              {formatSar(data.netCommission)} <SaudiRiyalSymbol className="ms-1 inline-block h-4 w-4 align-middle text-primary" />
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.vat', { defaultValue: 'Value Added Tax (VAT)' })}</dt>
            <dd className="font-medium text-primary tabular-nums">
              {formatSar(data.vat)} <SaudiRiyalSymbol className="ms-1 inline-block h-4 w-4 align-middle text-primary" />
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70 font-semibold">{t('payer.total', { defaultValue: 'Total' })}</dt>
            <dd className="font-semibold text-secondary tabular-nums">
              {formatSar(data.total)} <SaudiRiyalSymbol className="ms-1 inline-block h-4 w-4 align-middle text-secondary" />
            </dd>
          </div>
        </dl>
      </section>

      {/* Property Details */}
      <section
        className="w-full rounded-card border border-surface-border bg-surface-cards p-6 sm:p-8 flex flex-col gap-4"
        aria-labelledby="payer-property-heading"
      >
        <h2 id="payer-property-heading" className="font-sans text-lg font-semibold text-primary flex items-center gap-2 m-0">
          <HugeiconsIcon icon={Building01Icon} size={22} className="text-primary" />
          {t('payer.propertyDetails', { defaultValue: 'Property Details' })}
        </h2>
        <dl className="grid gap-2 font-sans text-sm m-0">
          <div className="flex flex-col gap-1">
            <dt className="text-primary/70">{t('payer.propertyName', { defaultValue: 'Property Name' })}</dt>
            <dd className="font-medium text-primary m-0">{data.propertyName}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.deedNumber', { defaultValue: 'Deed Number' })}</dt>
            <dd className="font-medium text-primary">{data.deedNumber}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.city', { defaultValue: 'City' })}</dt>
            <dd className="font-medium text-primary">{data.city}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.district', { defaultValue: 'District' })}</dt>
            <dd className="font-medium text-primary">{data.district}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-primary/70">{t('payer.propertyValue', { defaultValue: 'Property Value' })}</dt>
            <dd className="font-semibold text-secondary tabular-nums">
              {formatSar(data.propertyValue)} <SaudiRiyalSymbol className="ms-1 inline-block h-4 w-4 align-middle text-secondary" />
            </dd>
          </div>
          {data.description && (
            <div className="flex flex-col gap-1 pt-2 border-t border-primary/10">
              <dt className="text-primary/70">{t('payer.description', { defaultValue: 'Description' })}</dt>
              <dd className="font-normal text-primary m-0 leading-relaxed">{data.description}</dd>
            </div>
          )}
        </dl>
      </section>

      {/* Payment Instructions */}
      <section
        className="w-full rounded-card border border-surface-border bg-surface-cards p-6 sm:p-8 flex flex-col gap-4"
        aria-labelledby="payer-payment-heading"
      >
        <h2 id="payer-payment-heading" className="font-sans text-lg font-semibold text-primary flex items-center gap-2 m-0">
          <HugeiconsIcon icon={BankIcon} size={22} className="text-primary" />
          {t('payer.paymentInstructions', { defaultValue: 'Payment Instructions' })}
        </h2>

        {isPaid ? (
          <>
            <InlineSuccessCallout
              contentDir={payerContentDir}
              title={t('payer.paymentReceived', {
                defaultValue: 'Payment received. You can download the official invoice below.',
              })}
            />
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              className="rounded-[16px]"
              icon={<HugeiconsIcon icon={ArrowDown01Icon} size={18} />}
              iconPosition={i18n.language.startsWith('ar') ? 'left' : 'right'}
            >
              {t('payer.downloadInvoice', { defaultValue: 'Download official invoice' })}
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-base font-medium text-primary tracking-wide" dir="ltr">
                {data.virtualIban}
              </span>
              <Button
                type="button"
                variant="outlined"
                size="md"
                onClick={handleCopyIban}
                icon={<HugeiconsIcon icon={Copy01Icon} size={18} />}
                iconPosition="right"
              >
                {copyDone ? t('payer.copied', { defaultValue: 'Copied' }) : t('payer.copy', { defaultValue: 'Copy' })}
              </Button>
            </div>
            <InlineToastCallout
              variant="info"
              contentDir={payerContentDir}
              description={t('payer.depositAlert', {
                defaultValue:
                  'Deposit the total commission amount to the IBAN number above using the bank transfer service.',
              })}
            />
          </>
        )}
      </section>

      {isPaid && (
        <>
          {!isRefunded ? (
            <PayerRefundFlowModal
              open={refundModalOpen}
              onClose={() => setRefundModalOpen(false)}
              dealId={dealId}
              paymentToken={token}
              totalCommission={data.total}
              onCompleted={() => void queryClient.invalidateQueries({ queryKey: queryKeys.payerDeal(dealId) })}
            />
          ) : null}

          {/* Escrow — hidden once refund is approved (Refunded) */}
          {!isRefunded ? (
            <section
              className="w-full rounded-card border border-surface-border bg-surface-cards p-6 sm:p-8 flex flex-col gap-4"
              aria-labelledby="payer-escrow-heading"
            >
              <h2 id="payer-escrow-heading" className="font-sans text-lg font-semibold text-primary flex items-center gap-2 m-0">
                <HugeiconsIcon icon={WalletIcon} size={22} className="text-primary" />
                {t('payer.escrowFunds', { defaultValue: 'Escrow Funds' })}
              </h2>
              {isTransferConfirmed && transferConfirmedAt ? (
                <InlineSuccessCallout
                  contentDir={payerContentDir}
                  title={
                    <Trans
                      i18nKey="payer.propertyTransferConfirmedOn"
                      values={{ date: formatConfirmedAt(transferConfirmedAt, resolvedLng) }}
                      components={[
                        <span
                          key="payer-property-transfer-date"
                          dir="ltr"
                          className="tabular-nums inline-block"
                        />,
                      ]}
                    />
                  }
                  description={t('payer.escrowCompletedSummary', {
                    defaultValue: 'Commission funds have been released to the brokers.',
                  })}
                />
              ) : (
                <>
                  <p className="font-sans text-sm text-primary/80 m-0">
                    {refundAlreadyRequested
                      ? t('payer.escrowNoteDuringRefund', {
                          defaultValue:
                            'Funds remain held in escrow. While your refund request is under review, confirming property transfer is not available.',
                        })
                      : t('payer.escrowNote', {
                          defaultValue:
                            'Funds are securely held by Saee and will be released to brokers only after your confirmation of property transfer.',
                        })}
                  </p>
                  {refundAlreadyRequested ? null : (
                    <Button
                      type="button"
                      variant="primary"
                      size="lg"
                      fullWidth
                      className="rounded-[16px]"
                      disabled={releaseLoading}
                      loading={releaseLoading}
                      onClick={() => void handleConfirmPropertyTransfer()}
                    >
                      {t('payer.confirmPropertyTransfer', { defaultValue: 'Confirm Property Transfer' })}
                    </Button>
                  )}
                </>
              )}
            </section>
          ) : null}

          {/* Refund */}
          {isRefunded ? (
            <section
              className="w-full rounded-card border border-secondary/25 bg-secondary/5 p-6 sm:p-8 flex flex-col gap-3"
              role="status"
              aria-labelledby="payer-refund-processed-heading"
            >
              <h2
                id="payer-refund-processed-heading"
                className="font-sans text-lg font-semibold text-primary flex items-center gap-2 m-0"
              >
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={22} className="text-secondary shrink-0" />
                {t('payer.refundNote', { defaultValue: 'Note' })}
              </h2>
              <p className="font-sans text-sm text-primary/90 m-0 leading-relaxed" dir="auto">
                {t('payer.refundProcessed', {
                  defaultValue:
                    'The refund has been processed and will be executed within five business days.',
                })}
              </p>
            </section>
          ) : !refundAlreadyRequested && !isTransferConfirmed ? (
            <section
              className="w-full rounded-card border border-amber-200 bg-surface-cards p-6 sm:p-8 flex flex-col gap-4"
              aria-labelledby="payer-refund-heading"
            >
              <h2 id="payer-refund-heading" className="font-sans text-lg font-semibold text-primary flex items-center gap-2 m-0">
                <HugeiconsIcon icon={AlertCircleIcon} size={22} className="text-amber-600" />
                {t('payer.refundNote', { defaultValue: 'Note' })}
              </h2>
              <p className="font-sans text-sm text-primary/80 m-0">
                {t('payer.refundText', { defaultValue: 'If the deal is not completed, you can request a full refund.' })}
              </p>
              <Button
                type="button"
                variant="outlined"
                size="lg"
                fullWidth
                className="rounded-[16px] border-primary text-primary"
                onClick={() => setRefundModalOpen(true)}
              >
                {t('payer.requestRefund', { defaultValue: 'Request Refund' })}
              </Button>
            </section>
          ) : refundAlreadyRequested && !isTransferConfirmed ? (
            <section
              className="w-full rounded-card border border-surface-border bg-surface-cards p-6 sm:p-8"
              role="status"
            >
              <p className="font-sans text-sm text-primary m-0">
                {t('payer.refundPending', {
                  defaultValue: 'Your refund request has been submitted and is under review.',
                })}
              </p>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
